import maxBy from "lodash.maxby";

import {
  AudienceAction,
  AudienceDto,
  AudienceFilterDto,
  AudienceType,
  CreateAudienceDto,
  ExpressionNodeType,
  ExpressionPayloadType,
  ExpressionType,
} from "@intelligems/ig-types";
import {
  expressionListToPostFix,
  postfixToExpressionList,
} from "@intelligems/ig-utils";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  initialAudienceFilterState,
  initialAudienceState,
} from "@src/api/initials";
import { combineAllAudienceSources } from "@src/features/sharedExperience/components/rollouts/utils";

import { IAudienceState, RolloutAudienceSources } from "./types";

export const initialState: IAudienceState = {
  isSuccess: false,
  audience: undefined,
  storedAudience: undefined,
  rolloutsInfo: undefined,
};

const audienceSlice = createSlice({
  name: "audience",
  initialState,
  reducers: {
    resetAudiences: () => {
      return initialState;
    },
    resetRolloutsInfo: (draft) => {
      draft.rolloutsInfo = undefined;
    },
    initAudiences: (
      draft,
      action: PayloadAction<{
        audience: AudienceDto | CreateAudienceDto | undefined | null;
        priceTestDefaultCurrency?: string;
      }>
    ) => {
      const { audience, priceTestDefaultCurrency } = action.payload;

      if (audience) {
        draft.audience = audience;
      } else {
        draft.audience = initialAudienceState(priceTestDefaultCurrency);
      }
      draft.storedAudience = draft.audience;
      draft.isSuccess = true;
    },
    initRolloutAudiences: (
      draft,
      action: PayloadAction<{
        originalAudience: AudienceDto | undefined | null;
        dashboardFilters: ExpressionPayloadType[] | undefined;
        analyticTableTargeting: ExpressionNodeType[] | undefined;
      }>
    ) => {
      const { originalAudience, dashboardFilters, analyticTableTargeting } =
        action.payload;

      draft.isSuccess = true;
      const generated = combineAllAudienceSources({
        dashboardFilters,
        experienceAudience: originalAudience || undefined,
        tableSelection: analyticTableTargeting,
        sourcesToBeUsed: {
          testTargeting: Boolean(originalAudience?.filters.length),
          dashboardTargeting: Boolean(dashboardFilters?.length),
          analyticTableTargeting: Boolean(analyticTableTargeting?.length),
        },
      });

      draft.storedAudience = originalAudience || undefined;
      draft.audience = {
        ...generated.generatedAudience,
        excludeCurrency: {
          exclude: originalAudience?.excludeCurrency?.exclude ?? false,
          currency: originalAudience?.excludeCurrency?.currency || null,
          country: originalAudience?.excludeCurrency?.country || undefined,
          region: originalAudience?.excludeCurrency?.region || undefined,
          city: originalAudience?.excludeCurrency?.city || undefined,
        },
      };
      draft.rolloutsInfo = {
        dashboardTargeting: expressionListToPostFix(dashboardFilters || []),
        analyticTableTargeting: analyticTableTargeting || [],
        sourcesUsed: generated.sourcesUsed,
      };
    },
    setSourcesToBeUsed: (
      draft,
      action: PayloadAction<RolloutAudienceSources>
    ) => {
      if (!draft.rolloutsInfo) return;

      const generated = combineAllAudienceSources({
        dashboardFilters: postfixToExpressionList(
          draft.rolloutsInfo?.dashboardTargeting || []
        ),
        experienceAudience: draft.storedAudience || undefined,
        tableSelection: draft.rolloutsInfo.analyticTableTargeting,
        sourcesToBeUsed: action.payload,
      });
      draft.audience = generated.generatedAudience;

      draft.rolloutsInfo.sourcesUsed = action.payload;
    },
    reorderAudienceFilters: (
      draft,
      action: PayloadAction<AudienceFilterDto[]>
    ) => {
      if (!draft.isSuccess || !draft.audience) return;

      draft.audience.filters = action.payload;
    },
    setAudienceCurrencyValue: (
      draft,
      action: PayloadAction<{ value: string; isMultiCurrency?: boolean }>
    ) => {
      if (!draft.isSuccess || !draft.audience) return;
      const { audience } = draft;
      const { value, isMultiCurrency } = action.payload;

      const selectedCurrencies =
        audience?.excludeCurrency?.currency?.split(",") || [];
      if (selectedCurrencies.includes(value)) {
        // remove from list
        const filtered = selectedCurrencies.filter(
          (currency) => currency !== action.payload.value
        );
        audience.excludeCurrency.currency =
          filtered.length > 0 ? filtered.toString() : null;
      } else {
        // add to list
        if (isMultiCurrency) {
          audience.excludeCurrency.currency = value;
        } else {
          selectedCurrencies.push(value);
          audience.excludeCurrency.currency = selectedCurrencies.toString();
        }
      }
      audience.excludeCurrency.exclude = !(
        audience.excludeCurrency.country === undefined &&
        audience.excludeCurrency.currency === null
      );
    },
    setAudienceCountryValue: (draft, action: PayloadAction<string>) => {
      if (!draft.isSuccess || !draft.audience) return;
      const { audience } = draft;

      const selectedCountries =
        audience?.excludeCurrency?.country?.split(",") || [];
      if (selectedCountries.includes(action.payload)) {
        // remove from list
        const filtered = selectedCountries.filter(
          (country) => country !== action.payload
        );
        audience.excludeCurrency.country =
          filtered.length > 0 ? filtered.toString() : undefined;
      } else {
        // add to list
        selectedCountries.push(action.payload);
        audience.excludeCurrency.country = selectedCountries.toString();
      }
      audience.excludeCurrency.exclude = !(
        audience.excludeCurrency.country === undefined &&
        audience.excludeCurrency.currency === null
      );
    },
    setAudienceElse: (
      draft,
      action: PayloadAction<{
        elseAction?: AudienceAction | null;
        elseVariationId?: string | null;
        elseExcludeFromAnalytics?: boolean;
      }>
    ) => {
      if (!draft.isSuccess || !draft.audience) return;

      draft.audience = {
        ...draft.audience,
        ...action.payload,
      };
    },
    setFilterAction: (
      draft,
      action: PayloadAction<{
        value: AudienceAction;
        index: number;
      }>
    ) => {
      if (!draft.isSuccess || !draft.audience) return;
      const { audience } = draft;
      const { value, index } = action.payload;

      audience.filters[index].action = value;
      audience.filters[index].excludeFromAnalytics = audience.filters.some(
        (filter) => filter.action === "assignVariation"
      );
    },
    setFilterVariation: (
      draft,
      action: PayloadAction<{
        value: string;
        index: number;
      }>
    ) => {
      if (!draft.isSuccess || !draft.audience) return;

      draft.audience.filters[action.payload.index].variationId =
        action.payload.value;
    },
    setFilterExcludeAnalytics: (
      draft,
      action: PayloadAction<{ value: boolean; index: number }>
    ) => {
      if (!draft.isSuccess || !draft.audience) return;
      const { value, index } = action.payload;

      draft.audience.filters[index].excludeFromAnalytics = value;
    },
    setEvaluationFrequency: (draft, action: PayloadAction<number | null>) => {
      if (!draft.isSuccess || !draft.audience) return;

      draft.audience.evaluationFrequency = action.payload;
    },
    setAudience: (draft, action: PayloadAction<AudienceDto>) => {
      if (!draft.isSuccess || !draft.audience) return;

      draft.audience = action.payload;
    },
    resetCustomAudienceState: (draft) => {
      if (!draft.audience) return;

      draft.audience = {
        ...initialAudienceState(),
        excludeCurrency: draft.audience.excludeCurrency,
        audienceType: "custom",
      };
    },
    setAudienceType: (draft, action: PayloadAction<AudienceType>) => {
      if (!draft.isSuccess || !draft.audience) return;

      draft.audience.audienceType = action.payload;
    },
    setAudiencePriceInit: (
      draft,
      action: PayloadAction<string | undefined>
    ) => {
      if (!draft.isSuccess || !draft.audience) return;

      draft.audience.excludeCurrency.exclude = true;
      draft.audience.excludeCurrency.currency = action.payload ?? null;
    },
    clearAudience: (draft) => {
      if (!draft.isSuccess || !draft.audience) return;

      draft.audience = {
        ...draft.audience,
        wholesale: false,
        filters: [],
        evaluationFrequency: null,
      };
    },
    clearAudienceCountryCurrency: (
      draft,
      action: PayloadAction<{
        defaultCurrency?: string;
      }>
    ) => {
      if (!draft.isSuccess || !draft.audience) return;
      const { excludeCurrency } = draft.audience;
      const { defaultCurrency } = action.payload;

      excludeCurrency.exclude = defaultCurrency !== undefined;
      excludeCurrency.currency = defaultCurrency ?? null;
      excludeCurrency.country = undefined;
    },
    setAudienceExpression: (
      draft,
      _action: PayloadAction<{
        expressionsList: ExpressionPayloadType[];
        filterIndex: number;
        expressionType?: ExpressionType;
        action?: AudienceAction;
        isPersonalization?: boolean;
      }>
    ) => {
      if (!draft.isSuccess || !draft.audience) return;
      const { audience } = draft;
      const {
        expressionsList,
        filterIndex,
        expressionType,
        action,
        isPersonalization,
      } = _action.payload;

      const postfix = expressionListToPostFix(expressionsList);
      if (!audience.filters[filterIndex]) {
        audience.filters.push({
          ...initialAudienceFilterState(),
          priority: filterIndex,
          expression: postfix,
          action:
            action ||
            (isPersonalization ? "experienceInclude" : "randomVariation"),
          ...(expressionType && {
            expressionType,
          }),
        });
      } else {
        audience.filters[filterIndex].expression = postfix;

        if (postfix.length === 0) {
          audience.filters.splice(filterIndex, 1);
          audience.filters = audience.filters.map((filter, index) => ({
            ...filter,
            priority: index,
          }));
        }

        if (audience.filters.length === 0) {
          draft.audience = {
            ...audience,
            wholesale: false,
            filters: [],
            evaluationFrequency: null,
          };
        } else {
          if (expressionType && audience.filters[filterIndex])
            audience.filters[filterIndex].expressionType = expressionType;
          if (action && audience.filters[filterIndex])
            audience.filters[filterIndex].action = action;
        }
      }
    },
    deleteFilterRow: (draft, action: PayloadAction<number>) => {
      if (!draft.isSuccess || !draft.audience) return;
      const { audience } = draft;

      audience.filters.splice(action.payload, 1);
      audience.filters = draft.audience.filters.map((item, i) => {
        return {
          ...item,
          priority: i,
        };
      });
    },
    updateAudienceRow: (
      draft,
      action: PayloadAction<{
        state: AudienceFilterDto;
        index: number;
      }>
    ) => {
      if (!draft.isSuccess || !draft.audience) return;
      const { filters } = draft.audience;
      const { state, index } = action.payload;

      if (index >= 0) {
        filters[index] = state;
      } else {
        filters.push({
          ...state,
          priority:
            filters.length === 0 ? 0 : maxBy(filters, "priority")!.priority + 1,
        });
      }
    },
  },
  selectors: {
    selectedAudienceState: (state) => state,
    selectedAudience: (state) => state.audience as AudienceDto,
    selectedAudienceRolloutInfo: (state) => state.rolloutsInfo,
  },
});

export const {
  resetAudiences,
  initAudiences,
  reorderAudienceFilters,
  deleteFilterRow,
  setAudienceElse,
  setFilterAction,
  setFilterVariation,
  setAudienceType,
  clearAudience,
  setAudienceCurrencyValue,
  setAudienceCountryValue,
  clearAudienceCountryCurrency,
  updateAudienceRow,
  setAudienceExpression,
  setAudience,
  setFilterExcludeAnalytics,
  setEvaluationFrequency,
  setAudiencePriceInit,
  initRolloutAudiences,
  setSourcesToBeUsed,
  resetCustomAudienceState,
  resetRolloutsInfo,
} = audienceSlice.actions;

export const audienceReducer = audienceSlice.reducer;
export const {
  selectedAudienceState,
  selectedAudience,
  selectedAudienceRolloutInfo,
} = audienceSlice.selectors;
